import { Typography, Grid, Button, styled } from '@ori-ui/mui';
import { useApiClient, updateCustomerRegistrationContactDetails, HttpStatusCodes } from '@ori/global-api-client-react';
import type {
  AxiosError,
  GlobalCustomerRegistrationsRegistrationContactDetailsDto,
} from '@ori/global-api-client-react';
import useTestId from '@ori/testid-generator';
import type { FC, ReactNode } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';

import { Localization } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { useConfiguration, useFormError, useNotificationData } from '../../contexts';
import { useTranslations } from '../../hooks/useTranslations_simplified-registration-BrandPartner';
import type { IErrors } from '../../interfaces';
import { MessageType } from '../../interfaces';
import { FetchResult, TranslationKey } from '../../models';
import { useContactMetadata, useContactData } from '../../models/api';
import { PhoneFieldByConfig, TextFormFieldByConfig } from '../FieldsByConfig';
import { StyledTitle } from './StepsStyledComponents';

const StyledSubTitle = styled(Typography)(({ theme }) => ({
  height: '80px',
  marginBottom: theme.spacing(10),
  textAlign: 'center',
}));

const SubmitButtton = styled(Button)(({ theme }) => ({
  minWidth: '330px',
  marginTop: theme.spacing(10),
}));

const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '500px',
  marginTop: theme.spacing(8),
}));

const StyledGridContainer = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(10),
}));
interface IVerificationChannelFormProps {
  children?: ReactNode;
  handlePrimaryChannel: () => void;
}

export const VerificationChannelForm: FC<IVerificationChannelFormProps> = (props) => {
  const { handlePrimaryChannel } = props;
  const { data: contactMetadata } = useContactMetadata();
  const { data: contactData } = useContactData();

  const { getTestIdProps } = useTestId();

  const methods = useForm<GlobalCustomerRegistrationsRegistrationContactDetailsDto>({
    reValidateMode: 'onSubmit',
    mode: 'onBlur',
  });

  const { formState } = methods;
  const { isSubmitting } = formState;

  const { formatMessage } = useTranslations();

  const api = useApiClient();

  const { registrationId } = useConfiguration();

  const {
    context: { errorData },
  } = useFormError();
  const { setErrorData } = errorData;

  const {
    context: { notificationData },
  } = useNotificationData();
  const { setNotificationData } = notificationData;

  const isEmailVerification = contactMetadata?.email?.isAvailable;

  const handleSubmitChannel: SubmitHandler<GlobalCustomerRegistrationsRegistrationContactDetailsDto> = async (data) => {
    const correctData: GlobalCustomerRegistrationsRegistrationContactDetailsDto = {
      ...data,
      isContactVerified: Boolean(contactData?.isContactVerified),
    };
    try {
      const updateContactResponse = await updateCustomerRegistrationContactDetails(api, registrationId, correctData);

      const result = new FetchResult<unknown>(updateContactResponse.status);

      if (result.isSuccess()) {
        handlePrimaryChannel();
      }
    } catch (error: unknown) {
      const errorResponse = error as AxiosError;
      if (errorResponse.response?.status === HttpStatusCodes.status400BadRequest) {
        const { Errors } = errorResponse.response.data as IErrors;
        const submitErrors = Errors?.map((er) => ({
          fieldName: er.PropertyName,
          errorCode: er.Code,
        }));
        if (submitErrors?.length && submitErrors.length > 0) {
          setErrorData(submitErrors);
        } else {
          setErrorData([]);
        }
      } else {
        setNotificationData({
          isOpen: true,
          message: (
            <Localization
              id={TranslationKey.cannotUpdate}
              defaultMessage={defaultTranslations.cannotUpdate}
            />
          ),
          type: MessageType.error,
        });
      }
    }
  };

  const content = isEmailVerification ? (
    <StyledGridContainer
      container
      justifyContent="center"
    >
      <Grid
        item
        xs={12}
        sm={12}
      >
        <StyledTitle
          variant="h4"
          color="textPrimary"
        >
          <Localization
            id={TranslationKey.EmailPrimaryChannelTitle}
            defaultMessage={defaultTranslations.EmailPrimaryChannelTitle}
          />
        </StyledTitle>
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
      >
        <StyledSubTitle
          variant="subtitle1"
          color="textPrimary"
        >
          <Localization
            id={TranslationKey.EmailPrimaryChannelDescription}
            defaultMessage={defaultTranslations.EmailPrimaryChannelDescription}
          />
        </StyledSubTitle>
      </Grid>

      <TextFormFieldByConfig
        enableLowercaseLetters
        name="contactEmail"
        label={
          <Localization
            id={TranslationKey.EmailPrimaryChannelLabel}
            defaultMessage={defaultTranslations.EmailPrimaryChannelLabel}
          />
        }
        tooltipText={
          <Localization
            id={TranslationKey.EmailPrimaryChannelTooltip}
            defaultMessage={defaultTranslations.EmailPrimaryChannelTooltip}
          />
        }
        customPlaceholder={formatMessage(TranslationKey.EmailPrimaryChannelPlaceholder)}
        keyboardType="email"
        {...contactMetadata.email}
      />
    </StyledGridContainer>
  ) : (
    <StyledGridContainer
      container
      justifyContent="center"
    >
      <Grid
        item
        xs={12}
        sm={12}
      >
        <StyledTitle
          variant="h4"
          color="textPrimary"
        >
          <Localization
            id={TranslationKey.PhonePrimaryChannelTitle}
            defaultMessage={defaultTranslations.PhonePrimaryChannelTitle}
          />
        </StyledTitle>
      </Grid>

      <Grid
        item
        xs={12}
        sm={12}
      >
        <StyledSubTitle
          variant="subtitle1"
          color="textPrimary"
        >
          <Localization
            id={TranslationKey.PhonePrimaryChannelDescription}
            defaultMessage={defaultTranslations.PhonePrimaryChannelDescription}
          />
        </StyledSubTitle>
      </Grid>
      <PhoneFieldByConfig
        name="contactTelephone"
        label={
          <Localization
            id={TranslationKey.PhonePrimaryChannelLabel}
            defaultMessage={defaultTranslations.PhonePrimaryChannelLabel}
          />
        }
        labelWithPrefix={
          <Localization
            id={TranslationKey.PhonePrimaryChannelLabelWithPrefix}
            defaultMessage={defaultTranslations.PhonePrimaryChannelLabelWithPrefix}
          />
        }
        tooltipText={
          <Localization
            id={TranslationKey.PhonePrimaryChannelTooltip}
            defaultMessage={defaultTranslations.PhonePrimaryChannelTooltip}
          />
        }
        tooltipWithPrefix={
          <Localization
            id={TranslationKey.PhonePrimaryChannelTooltipWithPrefix}
            defaultMessage={defaultTranslations.PhonePrimaryChannelTooltipWithPrefix}
          />
        }
        customPlaceholder={formatMessage(TranslationKey.PhonePrimaryChannelPlaceholder)}
        customPlaceholderWithPrefix={formatMessage(TranslationKey.PhonePrimaryChannelPlaceholderWithPrefix)}
        {...contactMetadata?.telephone}
      />
    </StyledGridContainer>
  );

  return (
    <FormProvider {...methods}>
      <StyledForm
        noValidate
        onSubmit={methods.handleSubmit(handleSubmitChannel)}
      >
        {content}
        <SubmitButtton
          variant="contained"
          color="primary"
          type="submit"
          disabled={isSubmitting}
          {...getTestIdProps('submit-btn-primaryChannel')}
        >
          <Localization
            id={TranslationKey.PrimaryChannelSubmitButton}
            defaultMessage={defaultTranslations.PrimaryChannelSubmitButton}
          />
        </SubmitButtton>
      </StyledForm>
    </FormProvider>
  );
};
